import { useState, useMemo } from 'react';
import { Send, Menu, MessageSquare, FileText, Phone, Calendar, Briefcase, AlertTriangle, Building2, Filter, X } from 'lucide-react';
import { useWhatsAppAnalytics } from '../../hooks/useWhatsApp.js';
import Card from '../../components/ui/Card.jsx';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const monthPresetLabels = {
  '1m': 'Last 1 Month',
  '3m': 'Last 3 Months',
  '6m': 'Last 6 Months'
};

const getMonthPresetRange = (presetKey) => {
  const monthsToSubtract = { '1m': 1, '3m': 3, '6m': 6 }[presetKey];
  if (!monthsToSubtract) {
    return null;
  }

  const now = new Date();
  const end = formatDateForInput(now);

  const startDateObj = new Date(now);
  startDateObj.setMonth(startDateObj.getMonth() - monthsToSubtract);
  const start = formatDateForInput(startDateObj);

  return { start, end };
};

function WhatsAppInsidePage() {
  // Date filter state
  const [filterType, setFilterType] = useState('all'); // 'all', 'date', 'month_preset', 'year', 'range'
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [monthPreset, setMonthPreset] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Memoize filters object to prevent infinite loops
  // Only recreate when filter values actually change
  const filters = useMemo(() => {
    if (filterType === 'date' && selectedDate) {
      return { date: selectedDate };
    }

    if (filterType === 'month_preset' && monthPreset) {
      const range = getMonthPresetRange(monthPreset);
      if (range) {
        return { startDate: range.start, endDate: range.end };
      }
    }

    if (filterType === 'year' && selectedYear) {
      return { year: parseInt(selectedYear, 10) };
    }

    if (filterType === 'range' && rangeStart && rangeEnd) {
      return { startDate: rangeStart, endDate: rangeEnd };
    }

    return {};
  }, [filterType, selectedDate, monthPreset, selectedYear, rangeStart, rangeEnd]);

  const activeFilterLabel = useMemo(() => {
    if (filterType === 'date' && selectedDate) {
      return `Specific Date: ${selectedDate}`;
    }
    if (filterType === 'month_preset' && monthPreset) {
      return monthPresetLabels[monthPreset] || 'Custom Month Range';
    }
    if (filterType === 'year' && selectedYear) {
      return `Year: ${selectedYear}`;
    }
    if (filterType === 'range' && rangeStart && rangeEnd) {
      return `Date Range: ${rangeStart} → ${rangeEnd}`;
    }
    return 'All Time';
  }, [filterType, selectedDate, monthPreset, selectedYear, rangeStart, rangeEnd]);

  const handleMonthPresetChange = (presetKey) => {
    if (monthPreset === presetKey && filterType === 'month_preset') {
      setMonthPreset('');
      setFilterType('all');
      return;
    }
    setMonthPreset(presetKey);
    setFilterType('month_preset');
    setSelectedDate('');
    setSelectedYear('');
    setRangeStart('');
    setRangeEnd('');
  };

  const handleSpecificDateChange = (value) => {
    setSelectedDate(value);
    if (value) {
      setFilterType('date');
      setMonthPreset('');
      setSelectedYear('');
      setRangeStart('');
      setRangeEnd('');
    } else if (!monthPreset && !selectedYear && !rangeStart && !rangeEnd) {
      setFilterType('all');
    }
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    if (value) {
      setFilterType('year');
      setSelectedDate('');
      setMonthPreset('');
      setRangeStart('');
      setRangeEnd('');
    } else if (!selectedDate && !monthPreset && !rangeStart && !rangeEnd) {
      setFilterType('all');
    }
  };

  const handleRangeChange = (field, value) => {
    const upcomingStart = field === 'start' ? value : rangeStart;
    const upcomingEnd = field === 'end' ? value : rangeEnd;

    if (field === 'start') {
      setRangeStart(value);
    } else {
      setRangeEnd(value);
    }

    if (upcomingStart || upcomingEnd) {
      setFilterType('range');
      setSelectedDate('');
      setMonthPreset('');
      setSelectedYear('');
    } else if (!selectedDate && !monthPreset && !selectedYear) {
      setFilterType('all');
    }
  };

  // Fetch WhatsApp analytics counts with filters
  // React Query automatically refetches when filters (query key) changes
  const { data: analyticsData, isLoading: analyticsLoading } = useWhatsAppAnalytics(filters);

  // Reset filters
  const resetFilters = () => {
    setFilterType('all');
    setSelectedDate('');
    setSelectedYear('');
    setRangeStart('');
    setRangeEnd('');
    setMonthPreset('');
    setShowFilters(false);
  };

  // Extract analytics counts from API response
  const counts = analyticsData?.data?.data || {
    order_document: 0,
    urgent_grievance: 0,
    government_decision: 0,
    form_selection: 0,
    circular_menu: 0,
    circular_document: 0,
    order_menu: 0,
    government_decision_menu: 0,
    company_adm: 0,
    grievance_priority: 0,
    salary_slip: 0,
    main_menu: 0,
    appointment_type: 0,
    form_document: 0,
    orderly_room: 0,
    leave_related: 0,
    contact_info: 0,
    sent: 0,
    received: 0
  };

  // Prepare data for Pie Chart (Menu Distribution)
  const menuCategories = [
    { name: 'Main Menu Count : मुख्य मेनू गणना', value: counts.main_menu || 0, label: 'Main Menu Count : मुख्य मेनू गणना' },
    { name: 'Total Sent Messages : पाठवलेले संदेश', value: counts.sent || 0, label: 'Total Sent Messages : पाठवलेले संदेश' },
    { name: 'Total Received Messages : प्राप्त संदेश', value: counts.received || 0, label: 'Total Received Messages : प्राप्त संदेश' },
    { name: 'परिपत्रक मेनू गणना', value: counts.circular_menu || 0, label: 'परिपत्रक मेनू गणना' },
    { name: 'आदेश मेनू गणना', value: counts.order_menu || 0, label: 'आदेश मेनू गणना' },
    { name: 'फॉर्म निवड मेनू गणना', value: counts.form_selection || 0, label: 'फॉर्म निवड मेनू गणना' },
    { name: 'शासन निर्णय मेनू गणना', value: counts.government_decision_menu || 0, label: 'शासन निर्णय मेनू गणना' },
    { name: 'Contact Info Count : संपर्क माहिती', value: counts.contact_info || 0, label: 'Contact Info Count : संपर्क माहिती' },
    { name: 'Salary Slip Count : वेतन पावती', value: counts.salary_slip || 0, label: 'Salary Slip Count : वेतन पावती' },
    { name: 'भेटीची वेळ प्रकार मेनू गणना', value: counts.appointment_type || 0, label: 'भेटीची वेळ प्रकार मेनू गणना' },
    { name: 'वरिष्ठ अधिकारी संपर्क मेनू गणना', value: counts.grievance_priority || 0, label: 'वरिष्ठ अधिकारी संपर्क मेनू गणना' },
  ];

  // Calculate total for percentage calculation
  const totalMenuCount = menuCategories.reduce((sum, item) => sum + item.value, 0);

  // Add percentage to each category
  const pieChartData = menuCategories
    .filter(item => item.value > 0) // Only show categories with data
    .map(item => ({
      ...item,
      percentage: totalMenuCount > 0 ? ((item.value / totalMenuCount) * 100).toFixed(1) : 0,
    }));

  // Prepare data for Bar Chart (Menu Type Comparison)
  const barChartData = [
    { name: 'Main Menu', count: counts.main_menu || 0 },
    { name: 'Total Sent Messages', count: counts.sent || 0 },
    { name: 'Total Received Messages', count: counts.received || 0 },
    { name: 'Circular Menu', count: counts.circular_menu || 0 },
    { name: 'Order Menu', count: counts.order_menu || 0 },
    { name: 'Form Menu', count: counts.form_selection || 0 },
    { name: 'Gov Decision Menu', count: counts.government_decision_menu || 0 },
    { name: 'Government Decision', count: counts.government_decision || 0 },
    { name: 'Salary Slip', count: counts.salary_slip || 0 },
    { name: 'Contact Info', count: counts.contact_info || 0 },
    { name: 'Appointment Type', count: counts.appointment_type || 0 },
    { name: 'Orderly Room', count: counts.orderly_room || 0 },
    { name: 'Leave Related', count: counts.leave_related || 0 },
    { name: 'Senior Officer Contact', count: counts.grievance_priority || 0 },
    { name: 'Urgent Grievance', count: counts.urgent_grievance || 0 },
    { name: 'Company Heads', count: counts.company_adm || 0 },
  ]; // Show all categories including those with 0 count

  // Colors for pie chart (matching the design)
  const COLORS = [
    '#FF9500', // Orange - Main Menu
    '#4ECDC4', // Teal - Order Menu
    '#FFD93D', // Yellow - Form Menu
    '#6BCAE2', // Light Blue - Circular Menu
    '#D2691E', // Brownish-orange - Senior Officer Contact
    '#1E3A8A', // Dark Blue - Gov Decision Menu
    '#FF6B35', // Dark Orange - Orderly Room
  ];

  // Custom label for pie chart showing percentage
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  // Get current year and month for defaults
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="mb-6 space-y-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                  WhatsApp Message Statistics
                </h1>
                <button
                  type="button"
                  onClick={() => setShowFilters(prev => !prev)}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Filter className="h-4 w-4" />
                  {showFilters ? 'Hide Filters' : 'Filter Options'}
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <span className="uppercase tracking-wide text-xs text-gray-500 dark:text-gray-400">
                  Active Filter
                </span>
                <span className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium">
                  {activeFilterLabel}
                </span>
              </div>
            </div>

            {showFilters && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                    <Filter className="h-4 w-4" />
                    Filter Options
                  </div>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-sm font-semibold text-red-600 dark:text-red-400 hover:underline"
                  >
                    <X className="h-4 w-4" />
                    Clear All
                  </button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      All Time
                    </p>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="w-full px-3 py-2 text-sm border border-transparent bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    >
                      Show All Data
                    </button>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Specific Date
                    </p>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => handleSpecificDateChange(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Month Presets
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(monthPresetLabels).map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleMonthPresetChange(key)}
                          className={`flex-1 min-w-[100px] px-3 py-2 text-sm rounded-lg border transition ${
                            monthPreset === key && filterType === 'month_preset'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Year
                    </p>
                    <input
                      type="number"
                      value={selectedYear}
                      onChange={(e) => handleYearChange(e.target.value)}
                      placeholder="e.g. 2025"
                      min="2000"
                      max={currentYear}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Date Range
                    </p>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <input
                        type="date"
                        value={rangeStart}
                        onChange={(e) => handleRangeChange('start', e.target.value)}
                        className="w-full sm:w-auto flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-500 dark:text-gray-400 text-sm">to</span>
                      <input
                        type="date"
                        value={rangeEnd}
                        onChange={(e) => handleRangeChange('end', e.target.value)}
                        className="w-full sm:w-auto flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {analyticsLoading ? (
          <div className="py-4 text-center text-gray-500 dark:text-gray-400">
            Loading statistics...
          </div>
        ) : (
          <div>
            {/* Statistics Cards (Full Width) */}
            <div className="space-y-6">
              {/* First Row - Main Menu Count (full width) */}
              <div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Menu className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Main Menu Count : मुख्य मेनू गणना</span>
                  </div>
                  <div className="text-2xl font-bold">
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">
                      {counts.main_menu || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Second Row - Sent and Received Messages (grid 2) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Send className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Total Sent Messages : पाठवलेले संदेश</span>
                  </div>
                  <div className="text-2xl font-bold">
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg">
                      {counts.sent || 0}
                    </span>
                  </div>
                </div>
                
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Total Received Messages : प्राप्त संदेश</span>
                  </div>
                  <div className="text-2xl font-bold">
                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg">
                      {counts.received || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Third Row - 2 cards (Circular Menu + Document) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    परिपत्रक मेनू गणना : 
                    <span className="ml-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg font-bold text-lg">
                      {counts.circular_menu || 0}
                    </span>
                  </h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Circular Document Count : परिपत्रक दस्तऐवज</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg">
                        {counts.circular_document || 0}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    आदेश मेनू गणना : 
                    <span className="ml-2 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg font-bold text-lg">
                      {counts.order_menu || 0}
                    </span>
                  </h3>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Order Document Count : आदेश दस्तऐवज</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg">
                        {counts.order_document || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Row - 2 cards (Form Selection + Document) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    फॉर्म निवड मेनू गणना : 
                    <span className="ml-2 px-3 py-1 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 rounded-lg font-bold text-lg">
                      {counts.form_selection || 0}
                    </span>
                  </h3>
                  <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                      <span className="text-xs font-medium text-cyan-700 dark:text-cyan-300">Form Document Count : फॉर्म दस्तऐवज</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-lg">
                        {counts.form_document || 0}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                    शासन निर्णय मेनू गणना : 
                    <span className="ml-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg font-bold text-lg">
                      {counts.government_decision_menu || 0}
                    </span>
                  </h3>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Government Decision Count : शासन निर्णय दस्तऐवज</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg">
                        {counts.government_decision || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fourth Row - 2 cards (Salary Slip + Contact Info) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">वेतन पावती गणना</h3>
                  <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                      <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Salary Slip Count : वेतन पावती</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg">
                        {counts.salary_slip || 0}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">कार्यालयाशी संपर्क गणना</h3>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="h-5 w-5 text-red-600 dark:text-red-400" />
                      <span className="text-xs font-medium text-red-700 dark:text-red-300">Contact Info Count : संपर्क माहिती</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg">
                        {counts.contact_info || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fifth Row - 2 cards (Appointment Type) */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                  भेटीची वेळ प्रकार मेनू गणना : 
                  <span className="ml-2 px-3 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-lg font-bold text-lg">
                    {counts.appointment_type || 0}
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-200 dark:border-teal-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                      <span className="text-xs font-medium text-teal-700 dark:text-teal-300">Orderly Room Count : ऑर्डरली रूम</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-lg">
                        {counts.orderly_room || 0}
                      </span>
                    </div>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Leave Related Count : कर्तव्य वर हजर होणे</span>
                    </div>
                    <div className="text-2xl font-bold">
                      <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg">
                        {counts.leave_related || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sixth Row - 2 cards (Grievance) */}
              <div>
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                  वरिष्ठ अधिकारी संपर्क मेनू गणना : 
                  <span className="ml-2 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg font-bold text-lg">
                    {counts.grievance_priority || 0}
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                      <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">Urgent Grievance Count : अतिशय तातडीचे</span>
                    </div>
                    <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                      {counts.urgent_grievance || 0}
                    </div>
                  </div>
                  <div className="bg-pink-50 dark:bg-pink-900/20 p-4 rounded-lg border border-pink-200 dark:border-pink-800">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                      <span className="text-xs font-medium text-pink-700 dark:text-pink-300">Company Heads Count : मध्यम</span>
                    </div>
                    <div className="text-2xl font-bold text-pink-900 dark:text-pink-100">
                      {counts.company_adm || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pie Chart Section - Before Bar Chart */}
        {!analyticsLoading && (
          <div className="mt-6">
            <Card>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Menu Distribution
              </h2>
              {pieChartData.length > 0 ? (
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-1">
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, props) => [
                            `${value} (${props.payload.percentage}%)`,
                            props.payload.label
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 flex flex-col justify-center">
                    <div className="space-y-2">
                      {pieChartData.map((entry, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {entry.label} ({entry.percentage}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                  No data available for pie chart
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Bar Chart Section - Below Pie Chart */}
        {!analyticsLoading && (
          <div className="mt-6">
            <Card>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Menu Type Comparison Chart
              </h2>
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={barChartData}
                    margin={{
                      top: 40,
                      right: 30,
                      left: 20,
                      bottom: 80,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      tick={{ fontSize: 11 }}
                      interval={0}
                    />
                    <YAxis
                      label={{ value: 'Counts', angle: -90, position: 'insideLeft' }}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => [value, 'Count']}
                      labelStyle={{ color: '#000' }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="count" 
                      fill="#4ECDC4" 
                      name="Count"
                      label={{ 
                        position: 'top', 
                        fill: '#000',
                        fontSize: 12,
                        fontWeight: 'bold'
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                  No data available for bar chart
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhatsAppInsidePage;

